<script lang="ts">
  import { Check, AlertTriangle, Download, MailCheck, MailQuestion, Sparkles } from "lucide-svelte";
  import Sheet from "./ui/Sheet.svelte";
  import Button from "./ui/Button.svelte";
  import Badge from "./ui/Badge.svelte";
  import { loadFeedback, type BenefitFeedback } from "./feedback";
  import { loadCardRequests, loadCustomCards, type CardRequest, type CustomCard } from "./profile";
  import { formatDate, formatRelativeTime } from "./lib/format";
  import type { WalletBenefit, WalletCard } from "../wallet/types";
  import { toast } from "./toast";

  export let open = false;
  export let onClose: () => void;
  export let allBenefits: WalletBenefit[];
  export let allCatalog: WalletCard[];

  let feedback: Record<string, BenefitFeedback> = {};
  let cardRequests: CardRequest[] = [];
  let customCards: CustomCard[] = [];

  $: if (open) {
    feedback = loadFeedback();
    cardRequests = loadCardRequests();
    customCards = loadCustomCards();
  }

  $: feedbackEntries = Object.values(feedback);
  $: usedCount = feedbackEntries.filter(f => f.kind === "used").length;
  $: wrongCount = feedbackEntries.filter(f => f.kind === "rule_wrong").length;
  $: requestCount = cardRequests.length;
  $: customCount = customCards.length;

  function nameFor(benefitId: string): { title: string; card: string } {
    const b = allBenefits.find(x => x.id === benefitId);
    if (!b) return { title: benefitId, card: "—" };
    const c = allCatalog.find(x => x.id === b.cardId);
    return { title: b.title, card: c?.shortName || c?.name || b.cardId };
  }

  function exportAll() {
    const payload = {
      exportedAt: new Date().toISOString(),
      schemaVersion: 1,
      feedback: feedbackEntries,
      cardRequests,
      customCards
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `mofaxiaoka-reports-${new Date().toISOString().slice(0, 10)}.json`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("已导出本地报告 JSON");
  }
</script>

<Sheet {open} title="你的反馈与报告" {onClose}>
  <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
      <p class="font-label text-muted-foreground">用过</p>
      <p class="mt-1 font-display text-2xl text-success">{usedCount}</p>
    </div>
    <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
      <p class="font-label text-muted-foreground">规则不对</p>
      <p class="mt-1 font-display text-2xl text-warn">{wrongCount}</p>
    </div>
    <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
      <p class="font-label text-muted-foreground">自录卡</p>
      <p class="mt-1 font-display text-2xl text-primary">{customCount}</p>
    </div>
    <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
      <p class="font-label text-muted-foreground">收录请求</p>
      <p class="mt-1 font-display text-2xl text-foreground">{requestCount}</p>
    </div>
  </div>

  <section class="mt-6">
    <h3 class="font-label mb-2 flex items-center gap-2 text-muted-foreground">
      <Sparkles class="size-3.5 text-primary" />
      权益反馈
    </h3>
    {#if feedbackEntries.length === 0}
      <p class="rounded-xl border border-dashed bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        还没打标。在 bucket 内点权益的「用过 / 规则不对」就会进这里。
      </p>
    {:else}
      <ul class="space-y-2">
        {#each feedbackEntries.sort((a, b) => b.reportedAt.localeCompare(a.reportedAt)) as f}
          {@const meta = nameFor(f.benefitId)}
          <li class="flex items-start gap-3 rounded-xl border bg-secondary/40 px-3 py-2.5">
            {#if f.kind === "used"}
              <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                <Check class="size-3" />
              </span>
            {:else}
              <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-warn/15 text-warn">
                <AlertTriangle class="size-3" />
              </span>
            {/if}
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-foreground">{meta.title}</p>
              <p class="mt-0.5 truncate text-[11px] text-muted-foreground">
                {meta.card} · {formatRelativeTime(f.reportedAt)}
              </p>
            </div>
            <Badge variant={f.kind === "used" ? "outline" : "destructive"} class="text-[10px] shrink-0">
              {f.kind === "used" ? "用过" : "规则不对"}
            </Badge>
          </li>
        {/each}
      </ul>
    {/if}
  </section>

  <section class="mt-6">
    <h3 class="font-label mb-2 flex items-center gap-2 text-muted-foreground">
      <MailCheck class="size-3.5 text-primary" />
      自录卡
    </h3>
    {#if customCount === 0}
      <p class="rounded-xl border border-dashed bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        你还没自录卡。
      </p>
    {:else}
      <ul class="space-y-2">
        {#each customCards as cc}
          <li class="rounded-xl border border-dashed bg-card/60 px-3 py-2.5">
            <div class="flex items-start justify-between gap-2">
              <p class="text-sm font-medium text-foreground">{cc.name}</p>
              <Badge variant="outline" class="text-[10px] shrink-0">{cc.level}</Badge>
            </div>
            <p class="mt-0.5 text-[11px] text-muted-foreground">
              {cc.issuer} · {cc.annualFeeText} · 自录于 {formatDate(cc.reportedAt)}
            </p>
          </li>
        {/each}
      </ul>
    {/if}
  </section>

  <section class="mt-6">
    <h3 class="font-label mb-2 flex items-center gap-2 text-muted-foreground">
      <MailQuestion class="size-3.5 text-primary" />
      收录请求
    </h3>
    {#if requestCount === 0}
      <p class="rounded-xl border border-dashed bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        还没提交收录请求。
      </p>
    {:else}
      <ul class="space-y-2">
        {#each cardRequests.sort((a, b) => b.createdAt.localeCompare(a.createdAt)) as req}
          <li class="rounded-xl border bg-card/60 px-3 py-2.5">
            <div class="flex items-start justify-between gap-2">
              <p class="text-sm font-medium text-foreground">{req.cardName}</p>
              <Badge
                variant={req.status === "submitted" ? "default" : req.status === "failed" ? "destructive" : "outline"}
                class="text-[10px] shrink-0"
              >
                {req.status === "submitted" ? "已提交" : req.status === "failed" ? "网络失败" : "本地排队"}
              </Badge>
            </div>
            <p class="mt-0.5 text-[11px] text-muted-foreground">
              {req.issuer || "未知发卡行"} · {formatRelativeTime(req.createdAt)}
            </p>
          </li>
        {/each}
      </ul>
    {/if}
  </section>

  <div class="mt-6 border-t border-dashed border-foreground/10 pt-4">
    <Button variant="outline" size="sm" class="w-full" on:click={exportAll}>
      <Download class="mr-1.5 size-3.5" />
      导出本地报告 (JSON)
    </Button>
    <p class="mt-2 text-center text-[11px] text-muted-foreground">
      仅本地下载。包含画像反馈和自录请求，不向我们上传。
    </p>
  </div>
</Sheet>
